import React, { useState } from 'react';
import { Star, ShoppingBag } from 'lucide-react';
import { PetStats } from './PetStats';
import { PurchaseModal } from '../../modals/PurchaseModal';
import { PurchaseSuccessModal } from '../../modals/PurchaseSuccessModal';
import { usePurchase } from '../../../hooks/usePurchase';
import type { Pet } from '../../../types/pets';
import type { User } from '../../../types/user';

interface PetCardProps {
  pet: Pet;
  user: User | null;
  onPurchaseComplete?: () => void;
}

export function PetCard({ pet, user, onPurchaseComplete }: PetCardProps) {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { purchasePet, isProcessing, error, clearError } = usePurchase(user);
  const disabled = !user || user.points < pet.basePrice;

  const handlePurchase = async () => {
    const success = await purchasePet(pet);
    if (success) {
      setShowPurchaseModal(false);
      setShowSuccessModal(true);
      onPurchaseComplete?.();
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
  };

  return (
    <>
      <div 
        className={`bg-gradient-to-br from-purple-500 to-pink-500 p-1 rounded-xl transform transition-all 
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
        onClick={() => !disabled && setShowPurchaseModal(true)}
      >
        <div className="bg-gray-900 p-6 rounded-lg h-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">{pet.name}</h3>
            <Star className={`w-5 h-5 ${
              pet.rarity === 'legendary' ? 'text-yellow-400' :
              pet.rarity === 'epic' ? 'text-purple-400' :
              pet.rarity === 'rare' ? 'text-blue-400' :
              pet.rarity === 'uncommon' ? 'text-green-400' :
              'text-gray-400'
            }`} />
          </div>

          <img
            src={pet.imageUrl}
            alt={pet.name}
            className="w-full h-48 object-cover rounded-lg mb-4"
          />

          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400 capitalize">{pet.type}</span>
              <div className="flex items-center gap-1 text-yellow-400">
                <ShoppingBag className="w-4 h-4" />
                <span className="font-mono">{pet.basePrice.toLocaleString()} coins</span>
              </div>
            </div>

            <PetStats stats={pet.stats} />
          </div>
        </div>
      </div>

      {showPurchaseModal && (
        <PurchaseModal
          item={pet}
          userPoints={user?.points || 0}
          onConfirm={handlePurchase}
          onCancel={() => {
            setShowPurchaseModal(false);
            clearError();
          }}
          isProcessing={isProcessing}
          error={error}
        />
      )}

      {showSuccessModal && (
        <PurchaseSuccessModal
          item={pet}
          onClose={handleSuccessClose}
        />
      )}
    </>
  );
}